import React from 'react';
import {
    BranchFork16Regular,
    CalendarLtr16Regular,
    Star16Regular,
    Eye16Regular,
    Money16Regular,
    ArrowCircleDown16Regular,
    PeopleTeam16Regular,
    Person16Regular,
    LayerDiagonalAddRegular,
    EditRegular,
    Copy20Regular,
    EyeOff16Regular,
    CheckmarkCircle16Filled,
    CheckmarkCircle24Filled,
    CheckmarkCircle24Regular,
} from "@fluentui/react-icons";
import { Button, Spinner } from '@fluentui/react-components';
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { useTranslation } from 'react-i18next';
import './ConceptList.Styles.css';
import { ConfirmationButtonComponent } from "../../../../commons/component/ConfirmationButton/ConfirmationButtonComponent";
import { ErrorType } from "../../../../models/HttpError";
import {
    getSettlementLabel,
    getValuationLabel,
} from '../ConceptGroupedView/ConceptGroupedView.Functions';


export interface IProps {
    Concept: ContractConcept;
    index: number;
    ConceptNameById?: Record<number, string>;
    isCloning?: boolean;
    isDeleting: boolean;
    failonDeleting: boolean;
    error: ErrorType | undefined;
    onEdit?: (stage: ContractConcept) => void;
    onDelete?: (stage: ContractConcept) => void;
    onClone?: (stage: ContractConcept) => void;
    onToggleEnabled?: (concept: ContractConcept, enabled: boolean) => void;
    onToggleApproved?: (concept: ContractConcept, approved: boolean) => void;
}


export const ConceptItemComponent = (props: IProps) => {
    const [t] = useTranslation();
    const cc = props.Concept;

    const isFixedValue = Number(cc.valuationType) === 1;
    const isPercentage = Number(cc.valuationType) === 3;
    const isByStage = Number(cc.valuationType) === 5;
    const currency = cc.conceptCurrency || '';

    const formatPrice = (v?: number) =>
        typeof v === 'number' && !isNaN(v)
            ? v.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
            : '';

    const showPrice = isFixedValue && typeof cc.priceValue === 'number' && !isNaN(cc.priceValue);
    const showPct = isPercentage && typeof cc.percentageValue === 'number' && !isNaN(cc.percentageValue);
    const includedInConceptId = typeof cc.incluedInContractConceptId === 'number' ? cc.incluedInContractConceptId : 0;
    const includedInConceptName = includedInConceptId > 0
        ? (props.ConceptNameById?.[includedInConceptId] || '').trim()
        : '';

    const executions: { key: string; label: string; active: boolean }[] = [
        { key: 'inFace', label: 'Presencial', active: !!cc.applyToInFaceExecution },
        { key: 'remote', label: 'Remoto',     active: !!cc.applyToRemoteExecution },
        { key: 'phone',  label: 'Telefónico', active: !!cc.applyToPhoneExecution },
    ];
    const activeExecutions = executions.filter(e => e.active);

    const appliesByBranch = !!cc.filterByBranches || (cc.branches?.length || 0) > 0;
    const branchNames = (cc.branches || [])
        .map((branch) => {
            const cleanName = (branch.branchName || '').trim();
            return cleanName || `#${branch.branchId}`;
        })
        .filter((value, index, all) => value.length > 0 && all.indexOf(value) === index);

    const branchSummary = appliesByBranch
        ? (branchNames.length > 0 ? branchNames.join(', ') : t('conceptList.badges.noBranchAssigned', 'Sin rama asignada'))
        : t('conceptList.badges.allBranches', 'Todas las ramas');

    const handleOpenEdit = () => {
        props.onEdit?.(cc);
    };

    // Enabled defaults to true so concepts saved before the flag existed do not all
    // render as disabled; approval is opt-in, so it defaults to false.
    const isEnabled = cc.contractConceptEnabled !== false;
    const isApproved = cc.contractConceptApproved === true;

    return (
        <div
            className={[
                'coi-card',
                isApproved ? 'coi-card--approved' : '',
                isEnabled ? '' : 'coi-card--disabled',
            ].filter(Boolean).join(' ')}
            key={cc.id}
            role="button"
            tabIndex={0}
            onClick={handleOpenEdit}
            onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenEdit();
                }
            }}
        >
            <div className="coi-order">
                <span className="coi-order-num">{props.index + 1}</span>
                <span className="coi-order-label">{t('conceptItem.props.concept')}</span>
                {/* The card itself opens the editor, so this stops the click from
                    bubbling the way the action strip already does. */}
                <div
                    className="coi-review"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <Button
                        appearance={isApproved ? 'primary' : 'outline'}
                        shape='circular'
                        size='large'
                        className={isApproved ? 'coi-review-btn coi-review-btn--on' : 'coi-review-btn'}
                        icon={isApproved ? <CheckmarkCircle24Filled /> : <CheckmarkCircle24Regular />}
                        aria-label={isApproved
                            ? t('conceptItem.flags.unapproveAction')
                            : t('conceptItem.flags.approveAction')}
                        title={isApproved
                            ? t('conceptItem.flags.unapproveAction')
                            : t('conceptItem.flags.approveAction')}
                        onClick={() => props.onToggleApproved?.(cc, !isApproved)}
                    />
                </div>
            </div>

            <div className="coi-body">

                {/* Row 1: name + code */}
                <div className="coi-title-row">
                    <span className="coi-name">{cc.concept?.conceptName}</span>
                    {cc.concept?.conceptCode && (
                        <span className="coi-code">{cc.concept.conceptCode}</span>
                    )}
                    {isApproved && (
                        <span className="coi-approved" title={t('conceptItem.flags.approvedTitle')}>
                            <CheckmarkCircle16Filled />
                            {t('conceptItem.flags.approved')}
                        </span>
                    )}
                    {!isEnabled && (
                        <span className="coi-disabled" title={t('conceptItem.flags.disabledTitle')}>
                            <EyeOff16Regular />
                            {t('conceptItem.flags.disabled')}
                        </span>
                    )}
                </div>

                {/* Row 2: description */}
                {cc.concept?.conceptDescription && (
                    <p className="coi-desc">{cc.concept.conceptDescription}</p>
                )}

                {/* Row 3: settlement + valuation type + executions */}
                <div className="coi-meta-row">
                    <span className="coi-meta-pill">
                        {getSettlementLabel(cc.settlementSetting)}
                    </span>
                    <span className="coi-meta-divider">·</span>
                    <span className="coi-meta-pill">
                        {getValuationLabel(cc.valuationType)}
                    </span>
                    {activeExecutions.length > 0 && (
                        <>
                            <span className="coi-meta-divider">·</span>
                            <span className="coi-exec-group">
                                {activeExecutions.map(e => (
                                    <span
                                        key={e.key}
                                        className="coi-exec-pill"
                                        title={`Aplica: ${e.label}`}
                                    >{e.label}</span>
                                ))}
                            </span>
                        </>
                    )}
                </div>

                {/* Row 4: feature badges */}
                <div className="coi-badges">
                    {!!cc.groupWithInTheVisit && (
                        <span className="coi-badge coi-badge--group"><BranchFork16Regular />{t('conceptList.badges.groupByVisit', 'Agrupado')}</span>
                    )}
                    {isByStage && (
                        <span className="coi-badge coi-badge--visit"><CalendarLtr16Regular />{t('conceptList.badges.byVisit', 'Por visita')}</span>
                    )}
                    {cc.isIncludeInVisit && (
                        <span className="coi-badge coi-badge--includeInVisit"><Eye16Regular />{t('conceptList.badges.includeInVisit', 'Inc. en visita')}</span>
                    )}
                    {cc.isIncludeInVisit && includedInConceptId > 0 && (
                        <span className="coi-badge coi-badge--includeInVisitConcept">
                            <Eye16Regular />
                            {includedInConceptName.length > 0
                                ? t('conceptList.badges.includedInConceptName', {
                                    conceptName: includedInConceptName,
                                    defaultValue: 'Se liquida en: {{conceptName}}'
                                })
                                : t('conceptList.badges.includedInConcept', {
                                    conceptId: includedInConceptId,
                                    defaultValue: 'Se liquida en concepto #{{conceptId}}'
                                })}
                        </span>
                    )}
                    {cc.isOptional && (
                        <span className="coi-badge coi-badge--optional"><Star16Regular />{t('conceptList.badges.optional', 'Opcional')}</span>
                    )}
                    {cc.isManagedByCoordinator && (
                        <span className="coi-badge coi-badge--coordinator"><Person16Regular />{t('conceptList.badges.coordinator', 'Coordinador')}</span>
                    )}
                    {cc.haveCost && (
                        <span className="coi-badge coi-badge--cost"><Money16Regular />{t('conceptList.badges.haveCost', 'Con costo')}</span>
                    )}
                    {cc.haveWithdrawal && (
                        <span className="coi-badge coi-badge--withdrawal"><ArrowCircleDown16Regular />{t('conceptList.badges.haveWithdrawal', 'Retención')}</span>
                    )}
                    {cc.haveDivisionFee && (
                        <span className="coi-badge coi-badge--division"><PeopleTeam16Regular />{t('conceptList.badges.divisionFee', 'Div. honorarios')}</span>
                    )}
                    {cc.haveOverhead && (
                        <span className="coi-badge coi-badge--overhead"><LayerDiagonalAddRegular />{t('conceptList.badges.overhead', 'Overhead')}</span>
                    )}
                    <span
                        className="coi-badge coi-badge--branch"
                        title={branchSummary}
                    >
                        <BranchFork16Regular />
                        {appliesByBranch
                            ? `${t('conceptList.badges.byBranch', 'Por rama')}: ${branchSummary}`
                            : t('conceptList.badges.allBranches', 'Todas las ramas')}
                    </span>
                </div>
            </div>

            {/* ── RIGHT: value + actions ── */}
            <div className="coi-aside">
                {showPrice && (
                    <div className="coi-value-chip">
                        <span className="coi-value-currency">{currency}</span>
                        <span className="coi-value-amount">{formatPrice(cc.priceValue)}</span>
                    </div>
                )}
                {showPct && (
                    <div className="coi-value-chip coi-value-chip--pct">
                        <span className="coi-value-amount">{cc.percentageValue}%</span>
                    </div>
                )}
                <div
                    className="coi-actions"
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => event.stopPropagation()}
                >
                    <Button
                        appearance='subtle'
                        size='small'
                        icon={isEnabled ? <Eye16Regular /> : <EyeOff16Regular />}
                        aria-label={isEnabled
                            ? t('conceptItem.flags.disableAction')
                            : t('conceptItem.flags.enableAction')}
                        title={isEnabled
                            ? t('conceptItem.flags.disableAction')
                            : t('conceptItem.flags.enableAction')}
                        onClick={() => props.onToggleEnabled?.(cc, !isEnabled)}
                    />
                    <Button
                        appearance='subtle'
                        size='small'
                        icon={props.isCloning ? <Spinner size='tiny' /> : <Copy20Regular />}
                        aria-label={t('conceptItem.actions.clone', 'Clonar concepto')}
                        disabled={props.isCloning}
                        onClick={() => props.onClone?.(cc)}
                    />
                    <Button
                        appearance='subtle'
                        size='small'
                        icon={<EditRegular />}
                        aria-label='Editar'
                        onClick={() => props.onEdit?.(cc)}
                    />
                    <ConfirmationButtonComponent
                        ButtonId={"deleteconcept" + cc.id}
                        ButtonIconName={"Delete"}
                        ButtonText={""}
                        ButtonCssClass={""}
                        ConfirmationButtonText={t('conceptItem.confirmationButton.deleteButton')}
                        ConfirmationButtonCssClass={""}
                        CancelButtonText={t('conceptItem.confirmationButton.cancelButton')}
                        CancelButtonCssClass={""}
                        Title={t('conceptItem.confirmationButton.tittle')}
                        Description={""}
                        ElementTextHeader={t('conceptItem.confirmationButton.deleteConcept') + cc.concept?.conceptName}
                        ElementTextInformation={t('conceptItem.confirmationButton.textInformation')}
                        onConfirmation={() => props.onDelete?.(cc)}
                        onCancel={() => {}}
                        isOperationExecuting={props.isDeleting}
                        isOperationExecutedWithError={false}
                        OperationError={undefined}
                    />
                </div>
            </div>
        </div>
    );
};